/**
 * QuickNavChip - Individual navigation chip
 *
 * A pressable chip that navigates to a specific section.
 * Includes active state styling and accessibility support.
 *
 * Phase 3.2: QuickNav Component
 */

import { Pressable, StyleSheet, ViewStyle } from 'react-native';
import { TierBody } from '@dramadetector/ui';

import type { SectionId } from './useScrollToSection';

interface QuickNavChipProps {
  /** Section identifier */
  sectionId: SectionId;
  /** Display label for the chip */
  label: string;
  /** Whether this chip is currently active/selected */
  isActive?: boolean;
  /** Callback when chip is pressed */
  onPress: (sectionId: SectionId) => void;
  /** Test ID for testing */
  testID?: string;
}

/**
 * Individual navigation chip with Y2K + Glassmorphism styling
 */
export function QuickNavChip({
  sectionId,
  label,
  isActive = false,
  onPress,
  testID,
}: QuickNavChipProps) {
  return (
    <Pressable
      onPress={() => onPress(sectionId)}
      accessibilityRole="button"
      accessibilityLabel={`Jump to ${label} section`}
      accessibilityState={{ selected: isActive }}
      testID={testID}
      style={({ pressed }) => [
        styles.chip,
        isActive && styles.chipActive,
        pressed && styles.chipPressed,
      ]}
    >
      <TierBody
        size="xs"
        style={[
          styles.chipText,
          isActive && styles.chipTextActive,
        ]}
      >
        {label}
      </TierBody>
    </Pressable>
  );
}

/**
 * StyleSheet for QuickNavChip
 *
 * Design: Y2K + Glassmorphism
 * Accessibility: WCAG AAA compliant
 * - Touch target: min 44x44 points
 * - Contrast: 7:1+ (gray-700 #374151 on white = 8.9:1)
 */
const styles = StyleSheet.create({
  chip: {
    paddingHorizontal: 16,
    paddingVertical: 12, // Increased for 44pt touch target
    minHeight: 44, // WCAG touch target
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderWidth: 1,
    borderColor: 'rgba(212, 165, 255, 0.2)',
    marginRight: 8,
    justifyContent: 'center',
  },

  chipActive: {
    backgroundColor: 'rgba(124, 58, 237, 0.15)',
    borderColor: 'rgba(124, 58, 237, 0.4)',
  },

  chipPressed: {
    opacity: 0.8,
    transform: [{ scale: 0.98 }],
  },

  chipText: {
    color: '#374151', // gray-700 (WCAG AAA: 8.9:1 contrast)
    fontWeight: '500',
  },

  chipTextActive: {
    color: '#5B21B6', // purple-800 (WCAG AAA: 9.4:1 contrast)
    fontWeight: '600',
  },
});
